import {
  DeleteOutlined,
  DollarOutlined,
  DownloadOutlined,
  EyeOutlined,
  FireOutlined,
  NotificationOutlined,
  ProductOutlined,
  SearchOutlined,
  ShoppingOutlined,
  ToolOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { debounce } from "lodash";
import { useCallback, useMemo, useState } from "react";
import ExcelIcon from "../../components/icons/ExcelIcon";
import FormIcon from "../../components/icons/FormIcon";
import PdfIcon from "../../components/icons/PdfIcon";
import PowerPointIcon from "../../components/icons/PowerPointIcon";
import WarehouseIcon from "../../components/icons/WarehouseIcon";
import WordIcon from "../../components/icons/WordIcon";
import ModalUploadDocument from "../../components/modal/ModalUploadDocument";
import { IDocument, IDocumentQuery } from "../../models/Document";
import {
  useDeleteDocument,
  useDownloadDocument,
  useGetAllDocument,
  useViewDocument,
} from "../../query/DocumentQuery";
import { notify } from "../../utils/toast";
import DocumentIcon from "../../components/icons/DocumentIcon";
import ImageIcon from "../../components/icons/ImageIcon";
import ZipIcon from "../../components/icons/ZipIcon";

const { Text } = Typography;

const Document = () => {
  const [filterCategory, setFilterCategory] = useState<
    IDocumentQuery["category"] | "all"
  >("all");
  const [params, setParams] = useState<IDocumentQuery>({
    page: 1,
    limit: 12,
  });
  const [viewModalOpen, setViewModalOpen] = useState<boolean>(false);
  const [viewModalUploadDocument, setViewModalUploadDocument] =
    useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<IDocument | null>(
    null,
  );
  const [viewUrl, setViewUrl] = useState<string>("");
  const downloadDocument = useDownloadDocument();
  const viewDocument = useViewDocument();
  const deleteDocument = useDeleteDocument();
  const { data: documentsData, isLoading } = useGetAllDocument(params);

  const handleDownloadFile = async (id: number) => {
    try {
      const res = await downloadDocument.mutateAsync(id);
      window.open(res, "_blank");
    } catch (error) {
      notify.error("Có lỗi xảy ra khi download");
    }
  };

  const handleViewDocument = async (doc: IDocument) => {
    const fileType = (doc.fileType || "").toLowerCase();
    setSelectedDocument(doc);
    setViewModalOpen(true);
    try {
      const url = await viewDocument.mutateAsync(doc.id);
      const isOfficeDoc =
        fileType.includes("wordprocessingml") ||
        fileType.includes("spreadsheetml") ||
        fileType.includes("presentationml") ||
        fileType.includes("msword") ||
        fileType.includes("excel") ||
        fileType.includes("powerpoint") ||
        fileType.endsWith(".doc") ||
        fileType.endsWith(".docx") ||
        fileType.endsWith(".xls") ||
        fileType.endsWith(".xlsx") ||
        fileType.endsWith(".ppt") ||
        fileType.endsWith(".pptx");
      if (isOfficeDoc) {
        const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
        setViewUrl(viewerUrl);
      } else {
        setViewUrl(url);
      }
    } catch (error) {
      notify.error("Có lỗi xảy ra khi xem file");
      setViewModalOpen(false);
    }
  };
  const handleDeleteDocument = async (id: number) => {
    try {
      await deleteDocument.mutateAsync(id);
      notify.success("Xóa thành công");
    } catch (error) {
      notify.error("Có lỗi xảy ra khi xóa");
    }
  };

  const handleCloseModal = useCallback(() => {
    setViewModalOpen(false);
    setSelectedDocument(null);
    setViewUrl("");
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setParams((prev) => ({
          ...prev,
          page: 1,
          name: value || undefined,
        }));
      }, 500),
    [],
  );
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedSearch(e.target.value);
    },
    [debouncedSearch],
  );
  const handleCategoryChange = useCallback((value: string) => {
    setFilterCategory(value as IDocumentQuery["category"] | "all");
    setParams((prev) => ({
      ...prev,
      page: 1,
      category:
        value === "all" ? undefined : (value as IDocumentQuery["category"]),
    }));
  }, []);

  const handlePageChange = useCallback((page: number, pageSize: number) => {
    setParams((prev) => ({
      ...prev,
      page,
      limit: pageSize,
    }));
  }, []);

  const getDocumentIcon = (doc: IDocument) => {
    const fileType = (doc.fileType || "").toLowerCase();
    const iconStyle = { fontSize: 64 };
    if (
      fileType.includes("wordprocessingml") ||
      fileType === "application/msword" ||
      fileType.endsWith(".doc") ||
      fileType.endsWith(".docx")
    ) {
      return <WordIcon style={iconStyle} />;
    }
    if (
      fileType.includes("spreadsheetml") ||
      fileType.includes("excel") ||
      fileType.endsWith(".xls") ||
      fileType.endsWith(".xlsx")
    ) {
      return <ExcelIcon style={iconStyle} />;
    }
    if (fileType === "application/pdf" || fileType.endsWith(".pdf")) {
      return <PdfIcon style={iconStyle} />;
    }
    if (
      fileType.includes("presentationml") ||
      fileType.includes("powerpoint") ||
      fileType.endsWith(".ppt") ||
      fileType.endsWith(".pptx")
    ) {
      return <PowerPointIcon style={iconStyle} />;
    }
    if (fileType.includes("image")) {
      return <ImageIcon style={iconStyle} />;
    }
    if (
      fileType === "application/zip" ||
      fileType === "application/x-zip-compressed"
    ) {
      return <ZipIcon style={iconStyle} />;
    }
    return <DocumentIcon style={iconStyle} />;
  };
  const getCategoryIcon = (
    category:
      | "Hành chính"
      | "Nhân sự"
      | "Kế toán"
      | "Marketing"
      | "Kỹ thuật"
      | "Sản xuất"
      | "Kho"
      | "Bán hàng"
      | "Khác",
  ) => {
    switch (category) {
      case "Hành chính":
        return (
          <Tag icon={<UserOutlined />} color="green">
            Hành chính
          </Tag>
        );
      case "Nhân sự":
        return (
          <Tag icon={<UserOutlined />} color="geekblue">
            Nhân sự
          </Tag>
        );
      case "Kế toán":
        return (
          <Tag icon={<DollarOutlined />} color="purple">
            Kế toán
          </Tag>
        );
      case "Marketing":
        return (
          <Tag icon={<NotificationOutlined />} color="orange">
            Marketing
          </Tag>
        );
      case "Kỹ thuật":
        return (
          <Tag icon={<ToolOutlined />} color="volcano">
            Kỹ thuật
          </Tag>
        );

      case "Sản xuất":
        return (
          <Tag icon={<ProductOutlined />} color="red">
            Sản xuất
          </Tag>
        );
      case "Kho":
        return (
          <Tag icon={<WarehouseIcon />} color="gold">
            Kho
          </Tag>
        );
      case "Bán hàng":
        return (
          <Tag icon={<ShoppingOutlined />} color="volcano">
            Bán hàng
          </Tag>
        );
      case "Khác":
        return (
          <Tag icon={<FormIcon />} color="default">
            Khác
          </Tag>
        );
      default:
        return (
          <Tag icon={<FormIcon />} color="default">
            Khác
          </Tag>
        );
    }
  };

  return (
    <Card
      title="Biểu mẫu & Tài liệu"
      extra={
        <Space>
          <Button
            icon={<UploadOutlined />}
            onClick={() => setViewModalUploadDocument(true)}
            type="primary"
          >
            Upload
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Row gutter={16}>
          <Col xs={24} md={16}>
            <Input
              size="large"
              placeholder="Tìm kiếm biểu mẫu..."
              prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
              onChange={handleSearchChange}
              style={{ borderRadius: 8 }}
            />
          </Col>
          <Col xs={24} md={8}>
            <Select
              size="large"
              value={filterCategory}
              onChange={handleCategoryChange}
              style={{ width: "100%", borderRadius: 8 }}
              options={[
                { label: "Tất cả", value: "all" },
                { label: "Hành chính", value: "Hành chính" },
                { label: "Nhân sự", value: "Nhân sự" },
                { label: "Kế toán", value: "Kế toán" },
                { label: "Marketing", value: "Marketing" },
                { label: "Kỹ thuật", value: "Kỹ thuật" },
                { label: "Sản xuất", value: "Sản xuất" },
                { label: "Kho", value: "Kho" },
                { label: "Bán hàng", value: "Bán hàng" },
                { label: "Khác", value: "Khác" },
              ]}
            />
          </Col>
        </Row>
        <Spin spinning={isLoading}>
          <Row gutter={[16, 16]} className="h-[720px] overflow-y-scroll">
            {documentsData?.data?.map((doc) => (
              <Col xs={24} sm={12} lg={6} key={doc.id}>
                <Card
                  style={{
                    borderRadius: 12,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                  bodyStyle={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "24px 16px",
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      cursor: "pointer",
                      zIndex: 1,
                    }}
                    onClick={() => handleViewDocument(doc)}
                  >
                    <Tooltip title="Xem">
                      <EyeOutlined
                        style={{
                          fontSize: 20,
                          color: "#ff8c00",
                        }}
                      />
                    </Tooltip>
                  </div>
                  <Popconfirm
                    title={`Bạn có chắc chắn muốn xóa tài liệu ${doc.documentName}`}
                    onConfirm={() => handleDeleteDocument(doc.id)}
                    okText="Có"
                    cancelText="Không"
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 48,
                        right: 12,
                        cursor: "pointer",
                        zIndex: 1,
                      }}
                    >
                      <Tooltip title="Xoá">
                        <DeleteOutlined
                          style={{
                            fontSize: 20,
                            color: "red",
                          }}
                        />
                      </Tooltip>
                    </div>
                  </Popconfirm>

                  <div style={{ marginBottom: 16 }}>{getDocumentIcon(doc)}</div>

                  <Text
                    strong
                    style={{
                      fontSize: 16,
                      textAlign: "center",
                      marginBottom: 12,
                      display: "block",
                    }}
                  >
                    {doc.documentName}
                  </Text>

                  <Space style={{ marginBottom: 16 }}>
                    {getCategoryIcon(doc.fileCategory)}
                    {doc.isHot && (
                      <Tag
                        icon={<FireOutlined />}
                        color="red"
                        style={{ borderRadius: 12 }}
                      >
                        Hot
                      </Tag>
                    )}
                  </Space>

                  <Space
                    direction="vertical"
                    size={4}
                    style={{
                      width: "100%",
                      marginBottom: 16,
                      textAlign: "center",
                    }}
                  >
                    <Space direction="horizontal">
                      <Text
                        type="secondary"
                        style={{
                          fontSize: 13,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <EyeOutlined
                          style={{ marginRight: 4 }}
                          onClick={() => handleViewDocument(doc)}
                        />
                        {doc.viewCount.toLocaleString()} lượt xem
                      </Text>
                      <Text
                        type="secondary"
                        style={{
                          fontSize: 13,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <DownloadOutlined style={{ marginRight: 4 }} />
                        {doc.downloadCount.toLocaleString()} lượt tải
                      </Text>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Cập nhật:{" "}
                      {new Date(doc.createdAt).toLocaleDateString("vi-VN")}
                    </Text>
                  </Space>

                  <Button
                    shape="round"
                    type="primary"
                    icon={<DownloadOutlined />}
                    block
                    color="green"
                    variant="solid"
                    onClick={() => handleDownloadFile(doc.id)}
                  >
                    Tải xuống
                  </Button>
                </Card>
              </Col>
            ))}
          </Row>
        </Spin>

        {documentsData?.pagination && (
          <div
            style={{
              marginTop: 24,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Pagination
              current={documentsData.pagination.page}
              pageSize={documentsData.pagination.limit}
              total={documentsData.pagination.total}
              onChange={handlePageChange}
              showSizeChanger
              showTotal={(total) => `Tổng ${total} tài liệu`}
            />
          </div>
        )}
      </Space>
      <ModalUploadDocument
        open={viewModalUploadDocument}
        onClose={() => setViewModalUploadDocument(false)}
      />
      <Modal
        title={selectedDocument?.documentName}
        open={viewModalOpen}
        onCancel={handleCloseModal}
        width="90%"
        style={{ top: 20 }}
        footer={[
          <Button key="close" onClick={handleCloseModal}>
            Đóng
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() =>
              selectedDocument && handleDownloadFile(selectedDocument.id)
            }
            loading={downloadDocument.isPending}
          >
            Tải xuống
          </Button>,
        ]}
      >
        {viewDocument.isPending ? (
          <div
            style={{
              height: "75vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Spin size="large" tip="Đang tải tài liệu..." />
          </div>
        ) : (
          viewUrl && (
            <div style={{ height: "75vh", width: "100%" }}>
              <iframe
                src={viewUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  borderRadius: 8,
                }}
                title={selectedDocument?.documentName}
                allow="autoplay"
              />
            </div>
          )
        )}
      </Modal>
    </Card>
  );
};

export default Document;
